import {
  AppWindowIcon,
  BanknoteIcon,
  BotIcon,
  BriefcaseIcon,
  ChartColumnIcon,
  CircleQuestionMarkIcon,
  ContrastIcon,
  CreditCardIcon,
  IdCardIcon,
  InboxIcon,
  ListChecksIcon,
  MailIcon,
  MessageSquareTextIcon,
  NetworkIcon,
  PlugIcon,
  RocketIcon,
  ShieldIcon,
  StoreIcon,
  TextAlignStartIcon,
  UsersIcon,
  WalletIcon,
} from "lucide-react";

import { COMMAND_ID as TOGGLE_COLOR_SCHEME_COMMAND_ID } from "@/app/app/_/commands/ToggleColorSchemeCommand";
import { Values } from "@/constants/values";
import { iconsByTypename } from "@/objects/@icons";
import { State } from "@/state";

const WAITLIST_PATH = "/auth/waitlist";

export type SidebarRoutingItem = {
  type: "routing";
  name: string;
  key: string;
  icon: React.ReactNode;
  path?: string;
  onClick?: () => void;
  comingSoon?: boolean;
  /** A stand-in while what it names loads: its words a skeleton, and it goes nowhere. */
  isLoading?: boolean;
};

export type SidebarItem =
  | SidebarRoutingItem
  | {
      type: "overline";
      name: string;
      isLoading?: boolean;
    }
  | {
      type: "divider";
    };

export type SettingsSidebarEntity = {
  identifier: string;
  name: string | null;
  isUserEntity: boolean;
  isAccountingFirm: boolean;
};

/**
 * What the settings sidebar draws while the person's entities load: their own
 * books, always first, and a business - each with the sections it will have.
 */
const LOADING_SETTINGS_ENTITIES: SettingsSidebarEntity[] = [
  {
    identifier: "loading-personal",
    name: "Your name",
    isUserEntity: true,
    isAccountingFirm: false,
  },
  {
    identifier: "loading-business",
    name: "Business name",
    isUserEntity: false,
    isAccountingFirm: false,
  },
];

/** The settings sidebar: the person's own sections, then each entity's; ``null`` entities while they load. */
export function buildSettingsSidebar(
  loadedEntities: SettingsSidebarEntity[] | null,
): SidebarItem[] {
  const isLoading = loadedEntities === null;
  const entities = loadedEntities ?? LOADING_SETTINGS_ENTITIES;
  // The person's own, above any one business: a mailbox is connected once and
  // given to whichever business it serves.
  const items: SidebarItem[] = [
    {
      type: "routing",
      key: "emails",
      name: "Emails",
      icon: <MailIcon size="1em" />,
      path: "/app/settings/emails",
    },
    {
      type: "routing",
      key: "accountant",
      name: "Your accountant",
      icon: <BriefcaseIcon size="1em" />,
      path: "/app/settings/accountant",
    },
  ];

  entities.forEach((entity) => {
    items.push({ type: "divider" });

    const label =
      (entity.name || entity.identifier) +
      (entity.isUserEntity ? " (you)" : "");

    items.push({ type: "overline", name: label, isLoading });

    const basePath = `/app/settings/entity/${entity.identifier}`;
    // A stand-in's sections lead nowhere until the entity they belong to is in.
    const placeholder = isLoading
      ? { path: undefined, isLoading: true, onClick: () => {} }
      : {};

    items.push({
      type: "routing",
      key: `${entity.identifier}-general`,
      name: "General",
      icon: <TextAlignStartIcon size="1em" />,
      path: basePath,
      ...placeholder,
    });

    if (entity.isUserEntity) {
      items.push({
        type: "routing",
        key: "profile",
        name: "Profile",
        icon: <IdCardIcon size="1em" />,
        path: "/app/settings/profile",
        ...placeholder,
      });
    }

    if (!entity.isUserEntity) {
      items.push({
        type: "routing",
        key: `${entity.identifier}-members`,
        name: "Members",
        icon: <UsersIcon size="1em" />,
        path: `${basePath}/members`,
        ...placeholder,
      });
    }

    // What its holders may do: an accountant in books shared with them, by
    // the role the client gives and the one their firm hands them on with.
    items.push({
      type: "routing",
      key: `${entity.identifier}-roles`,
      name: "Roles",
      icon: <ShieldIcon size="1em" />,
      path: `${basePath}/roles`,
      ...placeholder,
    });

    // An accounting or bookkeeping firm: the books clients share with it,
    // handed on to its people.
    if (entity.isAccountingFirm) {
      items.push({
        type: "routing",
        key: `${entity.identifier}-clients`,
        name: "Clients",
        icon: <BriefcaseIcon size="1em" />,
        path: `${basePath}/clients`,
        ...placeholder,
      });
    }

    items.push({
      type: "routing",
      key: `${entity.identifier}-templates`,
      name: "Invoice templates",
      icon: <AppWindowIcon size="1em" />,
      path: `${basePath}/templates`,
      ...placeholder,
    });

    items.push({
      type: "routing",
      key: `${entity.identifier}-integrations`,
      name: "Integrations",
      icon: <PlugIcon size="1em" />,
      path: `${basePath}/integrations`,
      ...placeholder,
    });

    items.push({
      type: "routing",
      key: `${entity.identifier}-billing`,
      name: "Billing",
      icon: <CreditCardIcon size="1em" />,
      path: `${basePath}/billing`,
      ...placeholder,
    });
  });

  return items;
}

const PLATFORM_ITEMS = [
  {
    name: "Agentic accounting",
    tagline: "Threads and workflows that close the books for you.",
    path: "/platform/agents",
    ReactIcon: BotIcon,
  },
  {
    name: "Triage & review",
    tagline: "Work through transactions in a focused inbox.",
    path: "/platform/triage",
    ReactIcon: InboxIcon,
  },
  {
    name: "Reports & dashboards",
    tagline: "Customizable widgets for P&L, cash flow, and more.",
    path: "/platform/reports",
    ReactIcon: ChartColumnIcon,
  },
  {
    name: "Multi-entity",
    tagline: "Intercompany transfers and reconciliation across entities.",
    path: "/platform/multi-entity",
    ReactIcon: NetworkIcon,
  },
  {
    name: "Ledger & integrations",
    tagline: "Bank sync, documents, and a full general ledger.",
    path: "/platform/ledger",
    ReactIcon: BanknoteIcon,
  },
];

const SOLUTION_ITEMS = [
  {
    name: "Multi-entity operators",
    tagline: "Holdings, personal + business, and intercompany.",
    path: "/solutions/multi-entity-operators",
    ReactIcon: NetworkIcon,
  },
  {
    name: "Finance & ops",
    tagline: "Triage, workflows, and agent-assisted close.",
    path: "/solutions/finance-ops",
    ReactIcon: ListChecksIcon,
  },
  {
    name: "Founders",
    tagline: "AI-assisted books and investor-ready reports.",
    path: "/solutions/founders",
    ReactIcon: RocketIcon,
  },
  {
    name: "Small businesses",
    tagline: "Financial clarity without the complexity.",
    path: "/solutions/small-businesses",
    ReactIcon: StoreIcon,
  },
];

const Navigation = {
  ROUTES: {
    AUTH: {
      VERIFY: "/auth/verify",
      LOGIN: "/auth/sign-in",
      SIGNUP: "/auth/sign-up",
      WAITLIST: "/auth/waitlist",
    },
    TOS: WAITLIST_PATH,
    PRIVACY_POLICY: WAITLIST_PATH,
    COMPLIANCE: WAITLIST_PATH,
    APP: {
      PAGE: "/app",
    },
    COMPANY: {
      TEAM: WAITLIST_PATH,
      CAREERS: WAITLIST_PATH,
      CONTACT: WAITLIST_PATH,
      SECURITY: WAITLIST_PATH,
      INVESTMENT: WAITLIST_PATH,
    },
    RESOURCES: {
      HELP_CENTER: WAITLIST_PATH,
      FAQ: WAITLIST_PATH,
      PRICING: WAITLIST_PATH,
      SUPPORT: WAITLIST_PATH,
    },
    PAGE: "/",
  },
  FOOTER_SECTIONS: [
    {
      name: "Products",
      items: PLATFORM_ITEMS,
    },
    {
      name: "Solutions",
      items: SOLUTION_ITEMS,
    },
    {
      name: "Resources",
      items: [
        { name: "Help Center", path: "/resources/help" },
        { name: "FAQ", path: "/resources/faq" },
        { name: "Support", path: "/resources/support" },
        // { name: 'Blog', path: '/resources/blog' },
        { name: "Pricing", path: "/pricing" },
      ],
    },
    {
      name: "Company",
      items: [
        { name: "Team", path: "/company/about" },
        { name: "Careers", path: "/company/careers" },
        { name: "Contact", path: "/company/contact" },
        { name: "Investment", path: "/company/investment" },
      ],
    },
  ],
  FOOTER_FOOTNOTES: [
    { name: "Terms of Service", path: "/policies/terms-and-conditions" },
    { name: "Privacy Policy", path: "/policies/privacy-policy" },
  ],
  PLATFORM_ITEMS: PLATFORM_ITEMS,
  SOLUTION_ITEMS: SOLUTION_ITEMS,
  /**
   * The app's pages that are none of its views, by path, as the top bar names
   * them: the views' state - the view last open - is left as it was there.
   */
  PAGES_OF_THEIR_OWN: {
    "/app/clients": "Your clients",
    "/app/invitations": "Invitations",
  } as Record<string, string>,
  SIDEBAR: [
    {
      type: "routing",
      name: "Threads",
      icon: <MessageSquareTextIcon size="1em" />,
      key: "thread",
    },
    {
      type: "routing",
      name: "Reports",
      icon: <ChartColumnIcon size="1em" />,
      key: "reports",
    },
    {
      type: "routing",
      name: "Triage",
      icon: <ListChecksIcon size="1em" />,
      key: "triage",
    },
    ...(Values.IS_BUDGET_SHOWN
      ? [
          {
            type: "routing",
            name: "Budget",
            icon: <WalletIcon size="1em" />,
            key: "budget",
          },
        ]
      : []),
    {
      type: "routing",
      name: "Rules",
      icon: <NetworkIcon size="1em" />,
      key: "rules",
    },
    {
      type: "overline",
      name: "Ledger",
    },
    {
      type: "routing",
      name: "Transactions",
      icon: <iconsByTypename.TransactionObject size="1em" />,
      key: "transactions",
    },
    {
      type: "routing",
      name: "Financial accounts",
      icon: <iconsByTypename.FinancialAccountObject size="1em" />,
      key: "financial-accounts",
    },
    {
      type: "routing",
      name: "Documents",
      icon: <iconsByTypename.DocumentObject size="1em" />,
      key: "documents",
    },
    {
      type: "routing",
      name: "Contacts",
      icon: <iconsByTypename.ContactObject size="1em" />,
      key: "contacts",
    },
    {
      type: "routing",
      name: "Organizations",
      icon: <iconsByTypename.OrganizationObject size="1em" />,
      key: "organizations",
    },
    {
      type: "overline",
      name: "Accounting",
    },
    {
      type: "routing",
      name: "Journal entries",
      icon: <iconsByTypename.JournalEntryObject size="1em" />,
      key: "journal-entries",
    },
    {
      type: "routing",
      name: "Chart of accounts",
      icon: <iconsByTypename.ChartedAccountObject size="1em" />,
      key: "chart-of-accounts",
    },
    {
      type: "routing",
      name: "Tax codes",
      icon: <iconsByTypename.TaxObject size="1em" />,
      key: "tax-codes",
    },
    {
      type: "divider",
    },
    {
      type: "routing",
      name: "Switch theme",
      icon: <ContrastIcon size="1em" />,
      key: "color-scheme",
      // The command palette's own toggle: light to dark, or back.
      onClick: () => {
        State.getState()
          .commandDefinitions.find(
            (command) => command.id === TOGGLE_COLOR_SCHEME_COMMAND_ID,
          )
          ?.onClick?.();
      },
    },
    {
      type: "routing",
      name: "Support",
      icon: <CircleQuestionMarkIcon size="1em" />,
      key: "support",
      onClick: () => {
        State.getState().setSnackbarMessage(
          "Email support@" + Values.URLS["EMAIL"],
        );
      },
    },
  ] as SidebarItem[],
};

export { Navigation };
